'use client';

import { useMemo, useRef, useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, CheckCircle } from 'lucide-react';
import type { FollowUpSendStepProps } from '../types';
import type {
  WorkflowInstitution,
  WorkflowDocument,
  BackendCorrespondence,
  CorrespondenceDraft
} from '@/lib/services/correspondences/workflows/workflow-types';
import type { Attachment } from '@/lib/services/correspondences/types';
import type { FileType } from '@/lib/services/files/types';
import { transformDocument } from '@/lib/services/correspondences/workflows/transformers';
import type { BatchActionItem } from '@/components/ui/workflow';
import { InstitutionSendCard } from '../../notifications-workflow/components/institution-send-card';
import { useFileUpload } from '@/lib/services/files/hooks';
import { toast } from '@/lib/toast';

interface ExtendedFollowUpSendStepProps extends FollowUpSendStepProps {
  showEmailOnly?: boolean;
  sendSenderCopy?: boolean;
  onSendSenderCopyChange?: (checked: boolean) => void;
}

export function SendFollowUpStep({
  matterId,
  institutionId,
  correspondenceId,
  workflowData,
  correspondence,
  onSend,
  onUpdate,
  onRefresh,
  isSending,
  sendError,
  showEmailOnly = true,
  sendSenderCopy = true,
  onSendSenderCopyChange
}: ExtendedFollowUpSendStepProps) {
  const [userLetterContent, setUserLetterContent] = useState<string | null>(
    null
  );
  const [userEmailSubject, setUserEmailSubject] = useState<string | null>(null);
  const [userEmailBody, setUserEmailBody] = useState<string | null>(null);
  const [additionalDocs, setAdditionalDocs] = useState<WorkflowDocument[]>([]);
  const [localSendingStatus, setLocalSendingStatus] = useState<
    'loading' | 'success' | 'error' | null
  >(null);

  // Local override from updateDraft response for immediate validation feedback
  const [localCorrespondenceOverride, setLocalCorrespondenceOverride] =
    useState<Partial<BackendCorrespondence> | null>(null);

  // Apply only validation and attachments from the override — the response
  // omits institution, signatoryUser, etc. which would corrupt the original
  const effectiveCorrespondence = useMemo(
    () =>
      localCorrespondenceOverride && correspondence
        ? {
            ...correspondence,
            ...(localCorrespondenceOverride.validation && {
              validation: localCorrespondenceOverride.validation
            }),
            ...(localCorrespondenceOverride.attachments && {
              attachments: localCorrespondenceOverride.attachments
            })
          }
        : correspondence,
    [localCorrespondenceOverride, correspondence]
  );

  // File upload hook for handling new file uploads
  const { uploadFile } = useFileUpload(matterId);

  // Get institution from workflow data
  const institution = useMemo(() => {
    return workflowData?.institutions?.find(
      (inst: WorkflowInstitution) => inst.id === institutionId
    );
  }, [workflowData, institutionId]);

  // Derived editable values: user override > correspondence prop.
  const letterContent =
    userLetterContent ?? correspondence?.correspondenceLetterBody ?? '';
  const emailSubject = userEmailSubject ?? correspondence?.subject ?? '';
  const emailBody =
    userEmailBody ?? correspondence?.fragments?.mainContent ?? '';

  // Derived sending status. A local override (from handleSend) wins until the
  // next correspondence prop change cycle resets it.
  const sendingStatus: 'pending' | 'loading' | 'success' | 'error' =
    localSendingStatus ??
    (correspondence?.status === 'sent'
      ? 'success'
      : isSending
        ? 'loading'
        : sendError
          ? 'error'
          : 'pending');

  // Clear local sending override and override-from-updateDraft when fresh data
  // arrives. Tracked via a ref so we only react to identity transitions, not
  // every render where setState would fire synchronously.
  const lastCorrespondenceRef = useRef(correspondence);
  /* eslint-disable react-hooks/refs -- React's documented "adjust state when props change" shape; see the comment above for why the ref guard is what keeps this on identity transitions. */
  if (lastCorrespondenceRef.current !== correspondence) {
    lastCorrespondenceRef.current = correspondence;
    if (localCorrespondenceOverride !== null) {
      setLocalCorrespondenceOverride(null);
    }
    if (localSendingStatus !== null) {
      setLocalSendingStatus(null);
    }
  }
  /* eslint-enable react-hooks/refs */

  // Get available documents from correspondence attachments
  const availableDocuments = useMemo(() => {
    return effectiveCorrespondence?.attachments?.map(transformDocument) || [];
  }, [effectiveCorrespondence]);

  // Get all matter documents for selection
  const matterDocuments = useMemo(() => {
    return workflowData?.availableDocuments || [];
  }, [workflowData]);

  // Handle letter content update
  const handleUpdateLetterContent = async (
    institutionIdStr: string,
    content: string,
    subject?: string,
    body?: string
  ) => {
    setUserLetterContent(content);
    if (subject !== undefined) setUserEmailSubject(subject);
    if (body !== undefined) setUserEmailBody(body);

    // Update correspondence via onUpdate
    try {
      await onUpdate({
        correspondenceLetterBody: content,
        subject: subject || emailSubject,
        fragments: {
          mainContent: body || emailBody
        }
      });
    } catch (error) {
      console.error('Failed to update correspondence:', error);
    }
  };

  // Handle document operations
  const handleAddDocument = (
    institutionIdStr: string,
    doc: WorkflowDocument
  ) => {
    setAdditionalDocs((prev) => [...prev, doc]);
  };

  const handleRemoveDocument = (institutionIdStr: string, docId: number) => {
    setAdditionalDocs((prev) => prev.filter((d) => d.id !== docId));
  };

  const handleAddCorrespondenceAttachment = async (
    institutionIdStr: string,
    doc: WorkflowDocument
  ) => {
    if (!matterId || !correspondenceId) return;

    try {
      // Add the attachment to the correspondence
      const currentAttachmentIds =
        correspondence?.attachments?.map((att: Attachment) => att.id) || [];

      // Check if document is already attached
      if (currentAttachmentIds.includes(doc.id)) {
        console.log('Document is already attached');
        return;
      }

      // Import the WorkflowDataService
      const WorkflowDataService = (
        await import('@/lib/services/correspondences/workflows/workflow-data.service')
      ).default;

      // Update the correspondence in the backend
      const updated = await WorkflowDataService.updateDraft(
        matterId,
        correspondenceId,
        {
          attachments: [...currentAttachmentIds, doc.id]
        } as unknown as Partial<CorrespondenceDraft>
      );

      // Immediately update local state with fresh validation from response
      if (updated) {
        setLocalCorrespondenceOverride(updated);
      }

      // Trigger a refetch to get updated data
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      console.error('Failed to add attachment:', error);
    }
  };

  const handleRemoveCorrespondenceAttachment = async (
    institutionIdStr: string,
    docId: number
  ) => {
    if (!matterId || !correspondenceId) return;

    try {
      // Remove the attachment from the correspondence
      const updatedAttachments =
        correspondence?.attachments?.filter(
          (att: Attachment) => att.id !== docId
        ) || [];

      // Import the WorkflowDataService
      const WorkflowDataService = (
        await import('@/lib/services/correspondences/workflows/workflow-data.service')
      ).default;

      // Update the correspondence in the backend
      const updated = await WorkflowDataService.updateDraft(
        matterId,
        correspondenceId,
        {
          attachments: updatedAttachments.map((att: Attachment) => att.id)
        } as unknown as Partial<CorrespondenceDraft>
      );

      // Immediately update local state with fresh validation from response
      if (updated) {
        setLocalCorrespondenceOverride(updated);
      }

      // Trigger a refetch to get updated data
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      console.error('Failed to remove attachment:', error);
    }
  };

  const handleUploadDocument = async (
    institutionIdStr: string,
    files: Array<{
      file: File;
      fileType: string;
      isCertifiedCopy: boolean;
    }>
  ) => {
    if (!matterId || !correspondenceId) return;

    try {
      for (const uploadItem of files) {
        // Step 1: Upload file to matter using the hook (handles S3 upload client-side)
        const result = await uploadFile(
          uploadItem.file,
          uploadItem.fileType as FileType,
          {
            matterId,
            displayName: uploadItem.file.name,
            isCertifiedCopy: uploadItem.isCertifiedCopy,
            attachmentId: correspondenceId
          }
        );

        if (!result) {
          throw new Error('Upload failed');
        }

        // Step 2: Attach the uploaded file to the correspondence
        const currentAttachmentIds =
          correspondence?.attachments?.map((att: Attachment) => att.id) || [];

        // Check if already attached
        if (!currentAttachmentIds.includes(result.id)) {
          // Import the WorkflowDataService
          const WorkflowDataService = (
            await import('@/lib/services/correspondences/workflows/workflow-data.service')
          ).default;

          const updated = await WorkflowDataService.updateDraft(
            matterId,
            correspondenceId,
            {
              attachments: [...currentAttachmentIds, result.id]
            } as unknown as Partial<CorrespondenceDraft>
          );

          // Immediately update local state with fresh validation from response
          if (updated) {
            setLocalCorrespondenceOverride(updated);
          }
        }
      }

      // Trigger a refetch to get updated data
      if (onRefresh) {
        await onRefresh();
      }

      toast.success(
        files.length === 1
          ? 'Attachment uploaded successfully'
          : `${files.length} attachments uploaded successfully`
      );
    } catch (error) {
      console.error('Failed to upload document:', error);
      toast.error(
        'Failed to upload attachment: ' +
          (error instanceof Error ? error.message : 'Unknown error')
      );
      throw error;
    }
  };

  // Handle signatory change (not used for follow-up, but required by InstitutionSendCard)
  const handleChangeSignatory = async (
    _institutionIdStr: string,
    _signatoryId: string
  ) => {
    // No-op for follow-up workflow as signatory is not required
    console.log('Signatory change not applicable for follow-up correspondence');
  };

  // Handle send
  const handleSend = async (_item: BatchActionItem) => {
    setLocalSendingStatus('loading');
    try {
      await onSend();
      setLocalSendingStatus('success');
    } catch (error) {
      console.error('Failed to send follow-up:', error);
      setLocalSendingStatus('error');
    }
  };

  if (!institution) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Institution not found. Please close and try again.
        </AlertDescription>
      </Alert>
    );
  }

  // Check if correspondence has been sent
  const isSent = correspondence?.status === 'sent';

  // Create batch action item for the InstitutionSendCard
  const batchItem: BatchActionItem = {
    id: institution.id.toString(),
    title: institution.name,
    description: '',
    status: sendingStatus,
    data: institution
  };

  return (
    <div className="space-y-6">
      {/* Success Message */}
      {isSent && (
        <Alert>
          <CheckCircle className="h-4 w-4" />
          <AlertDescription>
            Follow-up correspondence has been sent successfully to{' '}
            {institution.name}.
          </AlertDescription>
        </Alert>
      )}

      {/* Send Error */}
      {sendError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to send correspondence: {sendError.message}
          </AlertDescription>
        </Alert>
      )}

      {/* Institution Card */}
      <InstitutionSendCard
        institution={institution}
        correspondence={{
          item: batchItem,
          letterContent,
          emailSubject,
          emailBody,
          additionalDocs,
          availableDocuments,
          matterDocuments,
          correspondenceId,
          validation: effectiveCorrespondence?.validation
        }}
        signatoryState={{ current: '', options: [], hidden: true }}
        matter={{
          id: matterId,
          deceasedName: workflowData?.matter?.deceasedName,
          reference: workflowData?.matter?.referenceNumber
        }}
        selection={{ sendSenderCopy }}
        mode={{
          followUpWorkflow: true,
          emailOnly: showEmailOnly
        }}
        actions={{
          onUpdateLetterContent: handleUpdateLetterContent,
          onAddDocument: handleAddDocument,
          onUploadDocument: handleUploadDocument,
          onAddCorrespondenceAttachment: handleAddCorrespondenceAttachment,
          onRemoveDocument: handleRemoveDocument,
          onRemoveCorrespondenceAttachment:
            handleRemoveCorrespondenceAttachment,
          onChangeSignatory: handleChangeSignatory,
          onSend: handleSend,
          onSendSenderCopyChange
        }}
      />
    </div>
  );
}
